import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth, useSettings } from '../App';
import { Button } from './ui/button';
import { ClientShell } from './client/ClientShell';
import { ChannelDirectory } from './client/ChannelDirectory';
import { ChannelDetails } from './client/ChannelDetails';
import { 
  Tv, 
  Volume2, 
  VolumeX, 
  Maximize, 
  Play,
  Pause,
  RotateCcw
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import Hls from 'hls.js';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ChannelPlayer = () => {
  const { channelId } = useParams();
  const { user, logout } = useAuth();
  const { platformSettings } = useSettings();
  const navigate = useNavigate();
  const videoRef = useRef(null);
  
  const [channel, setChannel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolume] = useState(1);
  const [error, setError] = useState(null);
  const [hls, setHls] = useState(null);

  useEffect(() => {
    fetchChannel();
    return () => {
      if (hls) {
        hls.destroy();
      }
    };
  }, [channelId]);

  const fetchChannel = async () => {
    try {
      const response = await axios.get(`${API}/channels/${channelId}`);
      setChannel(response.data);
      
      // Initialize HLS player if needed
      if (response.data?.stream_url) {
        initializePlayer(response.data.stream_url);
      }
    } catch (error) {
      console.error('Error fetching channel:', error);
      if (error.response?.status === 401) {
        logout();
      } else if (error.response?.status === 404) {
        toast.error('Canal não encontrado');
        navigate('/dashboard');
      } else {
        setError('Erro ao carregar canal');
        toast.error('Erro ao carregar canal');
      }
    } finally {
      setLoading(false);
    }
  };

  const initializePlayer = (streamUrl) => {
    if (videoRef.current) {
      if (Hls.isSupported() && streamUrl.includes('.m3u8')) {
        const hlsInstance = new Hls({
          enableWebVTT: false,
          enableCEA708Captions: false,
        });
        
        hlsInstance.loadSource(streamUrl);
        hlsInstance.attachMedia(videoRef.current);
        
        hlsInstance.on(Hls.Events.MANIFEST_PARSED, () => {
          console.log('HLS manifest parsed, ready to play');
          setError(null);
        });

        hlsInstance.on(Hls.Events.ERROR, (event, data) => {
          console.error('HLS error:', data);
          if (data.fatal) {
            switch(data.type) {
              case Hls.ErrorTypes.NETWORK_ERROR:
                setError('Erro de rede ao carregar stream');
                break;
              case Hls.ErrorTypes.MEDIA_ERROR:
                setError('Erro de mídia no stream');
                break;
              default:
                setError('Erro fatal no player de vídeo');
                break;
            }
          }
        });

        setHls(hlsInstance);
      } else if (videoRef.current.canPlayType('application/vnd.apple.mpegurl')) {
        // Safari native HLS support
        videoRef.current.src = streamUrl;
      } else {
        // Fallback for non-HLS streams
        videoRef.current.src = streamUrl;
      }
    }
  };

  const handlePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play().catch(err => {
          console.error('Error playing video:', err);
          setError('Erro ao reproduzir stream');
        });
      }
    }
  };

  const handleVolumeToggle = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    if (videoRef.current) {
      videoRef.current.volume = newVolume;
    }
  };

  const handleFullscreen = () => {
    if (videoRef.current) {
      if (videoRef.current.requestFullscreen) {
        videoRef.current.requestFullscreen();
      } else if (videoRef.current.webkitRequestFullscreen) {
        videoRef.current.webkitRequestFullscreen();
      } else if (videoRef.current.mozRequestFullScreen) {
        videoRef.current.mozRequestFullScreen();
      }
    }
  };

  const handleReload = () => {
    if (channel?.stream_url) {
      if (hls) {
        hls.destroy();
      }
      setError(null);
      initializePlayer(channel.stream_url);
    }
  };

  const handleVideoLoad = () => {
    setError(null);
    setIsPlaying(true);
  };

  const handleVideoError = (e) => {
    console.error('Video error:', e);
    setError('Erro ao carregar stream. Verifique sua conexão.');
    setIsPlaying(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900 flex items-center justify-center">
        <div className="text-center">
          <div className="loading-spinner mx-auto mb-4" />
          <p className="text-white" role="status" data-testid="client-player-loading">Carregando canal...</p>
        </div>
      </div>
    );
  }

  if (!channel) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900 flex items-center justify-center">
        <div className="text-center">
          <Tv className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-white mb-2" data-testid="client-player-not-found">Canal não encontrado</h2>
          <Button onClick={() => navigate('/dashboard')} className="bg-blue-600 hover:bg-blue-700" data-testid="client-player-not-found-back">
            Voltar ao Dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <ClientShell watching>
      <main className="client-workspace" data-testid="client-watch-page">
        <section className="client-player-section" aria-label={channel.name}>
          <div className="client-player-stage" data-testid="client-player-stage">
            {error ? (
              <div className="client-player-error" role="alert" data-testid="client-player-error">
                <Tv aria-hidden="true" />
                <p>{error}</p>
                <Button onClick={handleReload} className="client-primary-button" data-testid="client-player-retry"><RotateCcw aria-hidden="true" />Tentar Novamente</Button>
              </div>
            ) : (
              <video
                ref={videoRef}
                className="client-video"
                data-testid="client-video"
                controls
                autoPlay
                muted={isMuted}
                onLoadStart={handleVideoLoad}
                onError={handleVideoError}
                onPlay={() => setIsPlaying(true)}
                onPause={() => setIsPlaying(false)}
              >
                {/* Sources will be set programmatically by HLS.js or fallback */}
                Seu navegador não suporta reprodução de vídeo.
              </video>
            )}
          </div>
          <div className="client-player-controls" role="group" aria-label="Controles do player" data-testid="client-player-controls">
            <div className="client-control-group">
              <Button variant="ghost" className="client-icon-button" onClick={handlePlay} title={isPlaying ? 'Pausar' : 'Reproduzir'} aria-label={isPlaying ? 'Pausar' : 'Reproduzir'} data-testid="client-player-play">{isPlaying ? <Pause aria-hidden="true" /> : <Play aria-hidden="true" />}</Button>
              <Button variant="ghost" className="client-icon-button" onClick={handleVolumeToggle} title={isMuted ? 'Ativar som' : 'Silenciar'} aria-label={isMuted ? 'Ativar som' : 'Silenciar'} data-testid="client-player-mute">{isMuted ? <VolumeX aria-hidden="true" /> : <Volume2 aria-hidden="true" />}</Button>
              <input type="range" min="0" max="1" step="0.1" value={volume} onChange={handleVolumeChange} className="client-volume" aria-label="Volume" data-testid="client-player-volume" />
            </div>
            <div className="client-control-group">
              <Button variant="ghost" className="client-icon-button" onClick={handleReload} title="Recarregar Stream" aria-label="Recarregar Stream" data-testid="client-player-reload"><RotateCcw aria-hidden="true" /></Button>
              <Button variant="ghost" className="client-icon-button" onClick={handleFullscreen} title="Tela Cheia" aria-label="Tela Cheia" data-testid="client-player-fullscreen"><Maximize aria-hidden="true" /></Button>
            </div>
          </div>
          <ChannelDetails channel={channel} user={user} handleFullscreen={handleFullscreen} handleReload={handleReload} />
        </section>
        <ChannelDirectory selectedChannelId={channelId} />
      </main>
    </ClientShell>
  );
};

export default ChannelPlayer;